'use client'
import React from 'react'
import { useLoaderStore } from '@/stores/useLoaderStore'

export interface IButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
    size?: 'sm' | 'md' | 'lg'
    color?: 'primary' | 'secondary' | 'tertiary' | 'white'
    /** Mostra o spinner no lugar do texto e desabilita (padrão do orbit). */
    isLoading?: boolean
}

const SIZES = {
    sm: 'text-[0.875rem] h-[1.563rem] px-4',
    md: 'text-[1rem] h-10 px-10',
    lg: 'text-[1.125rem] h-15 px-[5.438rem]',
}

const colorClass = (color: IButtonProps['color'], disabled: boolean | undefined, className: string) => {
    switch (color) {
        case 'primary':
            return `font-bold text-white rounded-full bg-harmonic-blue hover:bg-harmonic-blue-hover inline-flex items-center text-center justify-center ${className}`
        case 'tertiary':
            return `font-bold text-white rounded-full hover:bg-healthy-green-light/80 inline-flex items-center text-center ${
                disabled ? 'bg-healthy-green-light cursor-not-allowed' : 'bg-healthy-green'
            } `
        case 'white':
            return 'font-bold text-strong-blue rounded-full bg-white inline-flex items-center text-center border'
        default:
            return `flex justify-center font-bold rounded-full w-full inline-flex items-center text-center ${className}`
    }
}

/**
 * Botão da loja: azul (padrão), verde, branco ou sem cor, em três tamanhos. Também fica desabilitado enquanto o overlay
 * de carregamento está ligado (requisição com `meta.showLoader`), para nenhum formulário enviar de novo por baixo dele.
 */
const Button: React.FC<IButtonProps> = ({
    size = 'md',
    color = 'primary',
    isLoading = false,
    className = '',
    disabled,
    children,
    ...props
}) => {
    const globalLoading = useLoaderStore((state) => state.loading)

    return (
        <button
            {...props}
            className={`${SIZES[size]} ${colorClass(color, disabled, className)}`}
            disabled={globalLoading || isLoading || disabled}
            aria-busy={isLoading || undefined}
        >
            {isLoading ? (
                <>
                    <span className="loading loading-spinner loading-xs" aria-hidden="true" />
                    {/* Só o spinner aparece; o texto fica para o leitor de tela (nome acessível do botão). */}
                    <span className="sr-only">{children}</span>
                </>
            ) : (
                children
            )}
        </button>
    )
}

export default Button
